import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import {
  authAPI, patientsAPI, sessionsAPI, usersAPI, hospitalsAPI,
  setToken, clearToken,
} from '../services/api';
import { signIn, confirmSignIn, fetchAuthSession, signOut } from 'aws-amplify/auth';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

// Safely extract hospital name regardless of whether hospitalID is a
// populated object { name, hospitalID } or a bare ObjectId string.
export function getHospitalName(user) {
  if (!user?.hospitalID) return '';
  if (typeof user.hospitalID === 'object' && user.hospitalID !== null) {
    return user.hospitalID.name || '';
  }
  return ''; // bare ID — name not available yet
}

// Calculate age from ISO date string
export function calcAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const today = new Date();
  const dob   = new Date(dateOfBirth);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

// ─────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────
const initialState = {
  isAuthenticated:   false,
  authLoading:       true,   // true while bootstrapping from sessionStorage token
  currentUser:       null,   // full user object from /auth/me
  selectedPatient:   null,   // currently viewed patient object
  selectedSessions:  [],     // sessions for the selected patient
  toast:             null,
};

// ─────────────────────────────────────────────
// REDUCER
// ─────────────────────────────────────────────
function appReducer(state, action) {
  switch (action.type) {
    case 'AUTH_READY':
      return { ...state, authLoading: false };
    case 'LOGIN':
      return { ...state, isAuthenticated: true, authLoading: false, currentUser: action.user };
    case 'LOGOUT':
      return { ...initialState, authLoading: false };
    case 'UPDATE_USER':
      return { ...state, currentUser: { ...state.currentUser, ...action.updates } };
    case 'SET_SELECTED_PATIENT':
      return { ...state, selectedPatient: action.patient, selectedSessions: [] };
    case 'UPDATE_SELECTED_PATIENT':
      return { ...state, selectedPatient: { ...state.selectedPatient, ...action.updates } };
    case 'SET_SESSIONS':
      return { ...state, selectedSessions: action.sessions };
    case 'SHOW_TOAST':
      return { ...state, toast: action.toast };
    case 'CLEAR_TOAST':
      return { ...state, toast: null };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────
const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const toastTimer = useRef(null);

  // ── Bootstrap: restore session from Amplify on page refresh ──────────────
  // Amplify persists the Cognito session internally (in memory + cookie/storage
  // depending on config). On refresh, fetchAuthSession() retrieves the existing
  // session and auto-refreshes the token if it has expired.
  // This replaces the old sessionStorage token check.
  useEffect(() => {
    fetchAuthSession()
      .then(async (session) => {
        const token = session?.tokens?.idToken?.toString();
        if (!token) {
          dispatch({ type: 'AUTH_READY' });
          return;
        }
        setToken(token);
        const { user } = await authAPI.me();
        dispatch({ type: 'LOGIN', user });
      })
      .catch(() => {
        clearToken();
        dispatch({ type: 'AUTH_READY' });
      });
  }, []);

  const showToast = (message, type = 'info') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    dispatch({ type: 'SHOW_TOAST', toast: { message, type, id: Date.now() } });
    toastTimer.current = setTimeout(() => dispatch({ type: 'CLEAR_TOAST' }), 3500);
  };

  // ─────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────
  const actions = {

    // ── Auth ────────────────────────────────────────────────────────────────
    login: async (email, password) => {
      const lowerEmail = email.trim().toLowerCase();
      const result = await signIn({ 
        username: lowerEmail, 
        password,
      });

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Don't dispatch LOGIN yet -- they aren't logged in
        // Return a signal to the login page to redirect to change password
        return { requiresNewPassword: true };
      }

      // Normal login -- get token and fetch user from your MongoDB via backend
      const session = await fetchAuthSession();
      const token   = session.tokens.idToken.toString();
      setToken(token);
      const { user } = await authAPI.me();
      dispatch({ type: 'LOGIN', user });
      return { requiresNewPassword: false, role: user.role };
    },

    logout: async () => {
      await signOut();
      clearToken();
      dispatch({ type: 'LOGOUT' });
    },

    changePasswordChallenge: async (newPassword) => {
      await confirmSignIn({ challengeResponse: newPassword });
      // After confirming, they are now fully logged in
      const session = await fetchAuthSession();
      const token   = session.tokens.idToken.toString();
      setToken(token);
      const { user } = await authAPI.me();
      dispatch({ type: 'LOGIN', user });
    },

    changePassword: async (newPassword) => {
      await authAPI.changePassword(newPassword);

      showToast('Password updated successfully', 'success');
    },

    // ── Profile ─────────────────────────────────────────────────────────────
    updateProfile: async (data) => {
      const { user } = await usersAPI.updateMe(data);
      dispatch({ type: 'UPDATE_USER', updates: user });
      showToast('Profile updated', 'success');
    },

    // ── Patient selection (sets selected patient in global state) ────────────
    selectPatient: (patient) => {
      dispatch({ type: 'SET_SELECTED_PATIENT', patient });
    },

    // ── Patients — return data directly, pages manage their own loading state ─
    fetchPatients: (all = false) => patientsAPI.list(all),

    addPatient: async (data) => {
      const { patient } = await patientsAPI.create(data);
      return patient;
    },

    updatePatientCondition: async (patientId, prevMedicalCond) => {
      await patientsAPI.update(patientId, { prevMedicalCond });
      dispatch({ type: 'UPDATE_SELECTED_PATIENT', updates: { prevMedicalCond } });
      showToast('Medical conditions updated', 'success');
    },

    addNote: async (patientId, text) => {
      const { notes } = await patientsAPI.addNote(patientId, text);
      dispatch({ type: 'UPDATE_SELECTED_PATIENT', updates: { doctorNotes: notes } });
      showToast('Note added', 'success');
    },

    // ── Sessions ─────────────────────────────────────────────────────────────
    fetchSessions: async (patientId) => {
      const { sessions } = await sessionsAPI.listForPatient(patientId);
      dispatch({ type: 'SET_SESSIONS', sessions });
      return sessions;
    },

    // tracks = [{ taskSuffix, blob, fileName }]
    submitSession: async (patientId, tracks) => {
      const { session } = await sessionsAPI.create(patientId, tracks);
      // Prepend new session to list so it appears at top
      dispatch({ type: 'SET_SESSIONS', sessions: [session, ...state.selectedSessions] });
      showToast('Session uploaded successfully', 'success');
      return session;  // full session object including analysis field
    },

    // ── Users (Admin) ────────────────────────────────────────────────────────
    fetchDoctors: (hospitalID) => usersAPI.listDoctors(hospitalID),

    createDoctor: async (data) => {
      const result = await usersAPI.createDoctor(data);
      showToast('Doctor account created', 'success');
      return result;
    },

    updateDoctor: async (id, data) => {
      const result = await usersAPI.updateDoctor(id, data);
      showToast('Doctor updated', 'success');
      return result;
    },

    deactivateDoctor: async (id) => {
      await usersAPI.deactivateDoctor(id);
      showToast('Doctor deactivated', 'success');
    },

    // ── Hospitals (SuperAdmin) ───────────────────────────────────────────────
    fetchHospitals: () => hospitalsAPI.list(),

    createHospital: async (data) => {
      const result = await hospitalsAPI.create(data);
      showToast('Hospital created successfully', 'success');
      return result;
    },

    updateHospitalAdmin: async (hospitalId, data) => {
      const result = await hospitalsAPI.updateAdmin(hospitalId, data);
      showToast('Admin details updated', 'success');
      return result;
    },

    // ── Toast ────────────────────────────────────────────────────────────────
    showToast,
  };

  const role = state.currentUser?.role ?? null;

  return (
    <AppContext.Provider value={{
      state,
      actions,
      role,
      currentUser:      state.currentUser,
      selectedPatient:  state.selectedPatient,
      selectedSessions: state.selectedSessions,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};